import { WHY_CHOOSE, STATS } from "#/lib/constants";
import { Icon } from "#/components/shared/Icon";
import { SectionLabel } from "#/components/shared/SectionLabel";
import { useScrollAnimation, useCountUp } from "#/hooks/useScrollAnimation";

/**
 * Section 4 — compact "Why Blue Rock" banner (client direction 2026-07-13):
 * one-line heading + a single hairline-grid banner holding the 4 count-up
 * stats and the 4 trust bullets (icon + title only). Full descriptions live
 * on /services.
 */
export function WhyChooseUs() {
  const { ref, isVisible } = useScrollAnimation(0.1);

  return (
    <section className="relative overflow-hidden bg-graphite py-14 lg:py-20">
      <div className="blueprint-grid absolute inset-0 opacity-60" />

      <div ref={ref} className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionLabel tone="light">Why Blue Rock</SectionLabel>
        <h2
          className={`mt-4 max-w-3xl font-heading text-3xl font-extrabold leading-[1.1] text-white transition-all duration-700 sm:text-4xl ${
            isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
          }`}
        >
          Anyone can pour concrete. We pour it so it lasts.
        </h2>

        {/* Banner — stats row over bullet row, hairline grid */}
        <div
          className={`mt-8 overflow-hidden rounded-2xl border border-charcoal-light transition-all delay-150 duration-700 ${
            isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
          }`}
        >
          <div className="grid grid-cols-2 gap-px bg-charcoal-light lg:grid-cols-4">
            {STATS.map((stat) => (
              <StatItem key={stat.label} stat={stat} isVisible={isVisible} />
            ))}
          </div>
          <div className="grid grid-cols-1 gap-px border-t border-charcoal-light bg-charcoal-light sm:grid-cols-2 lg:grid-cols-4">
            {WHY_CHOOSE.map((feature) => (
              <div
                key={feature.title}
                className="flex items-center gap-3 bg-charcoal px-5 py-4"
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-flame/15 text-flame">
                  <Icon name={feature.icon} className="h-5 w-5" />
                </span>
                <h3 className="font-heading text-sm font-bold text-white">
                  {feature.title}
                </h3>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function StatItem({
  stat,
  isVisible,
}: {
  stat: { value: number; suffix: string; label: string };
  isVisible: boolean;
}) {
  const count = useCountUp(stat.value, isVisible, 1800);
  return (
    <div className="bg-graphite px-4 py-5 text-center">
      <div className="font-heading text-2xl font-extrabold text-white sm:text-3xl">
        {count}
        <span className="text-flame">{stat.suffix}</span>
      </div>
      <div className="mt-1 text-xs font-medium uppercase tracking-wide text-concrete">
        {stat.label}
      </div>
    </div>
  );
}
